import { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { auth } from '../firebase';
import { useContact } from '../contexts/ContactContext';
import SEO from '../components/SEO';

const HowItWorks = () => {
  const { firestoreUser } = useContext(UserContext);
  const currentAuthUser = auth.currentUser;
  const { openContact } = useContact();
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Show/hide "Back to Top" button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen howitworks-page" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="How It Works"
        description="How PedalxChange works: list your guitar pedals, discover new gear, send and receive trade or purchase offers, and complete deals safely with escrow protection."
        url="https://pedalxchange.com/how-it-works"
        keywords="how PedalxChange works, pedal trading process, trade guitar pedals, buy and sell guitar pedals, guitar pedal marketplace"
      />
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="howitworks-content-container" style={{ textAlign: 'center', marginBottom: '1rem', width: '100%' }}>
            <h1 className="text-3xl font-bold text-gray-900" style={{ textAlign: 'center', fontSize: '1.875rem', marginBottom: '2rem', lineHeight: '1.2' }}>
              How It Works
            </h1>
            
            {/* Table of Contents */}
            <nav style={{
              backgroundColor: 'rgba(201, 162, 39, 0.05)',
              borderRadius: '12px',
              padding: '1.5rem 2rem',
              marginBottom: '1rem',
              border: '1px solid rgba(201, 162, 39, 0.2)',
              maxWidth: '600px',
              margin: '0 auto 1rem auto'
            }}>
            <h2 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              marginBottom: '1rem',
              color: '#4A5759',
              fontFamily: 'Nunito, sans-serif',
              textAlign: 'center'
            }}>
              Three ways to get new gear on PXC:
            </h2>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
              <li>
                <a href="#direct-trade-offers" style={{
                  color: '#5A9ECB',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif'
                }} className="hover:underline">
                  Direct Trade Offers
                </a>
              </li>
              <li>
                <a href="#direct-purchase-offers" style={{
                  color: '#5A9ECB',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif'
                }} className="hover:underline">
                  Direct Purchase Offers
                </a>
              </li>
              <li>
                <a href="#trade-matches" style={{
                  color: '#5A9ECB',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontFamily: 'Nunito, sans-serif'
                }} className="hover:underline">
                  Trade Matches
                </a>
              </li>
            </ul>
            </nav>
          </div>
        </div>
        <style jsx>{`
          /* Desktop horizontal padding for all .max-w-7xl wrappers on this page.
             The Tailwind classes (px-6 sm:px-8 lg:px-8) on the wrappers are dead
             (Tailwind isn't actually wired up in this project), so without this
             rule there's zero horizontal padding on desktop — content slams to
             the browser edges at narrow desktop widths. Scoped to .howitworks-page
             so it doesn't affect other pages that share .max-w-7xl. */
          .howitworks-page .max-w-7xl {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
            box-sizing: border-box;
          }

          /* Two-column section balancing — the column sizes/gaps/justification
             are set as inline styles directly on each section's JSX. Only
             mobile fall-through guard rules are kept here. */

          @media (max-width: 768px) {
            .howitworks-section-container {
              flex-direction: column !important;
              gap: 1rem !important;
            }
            .howitworks-content-container {
              width: 100% !important;
              padding: 0 1rem !important;
              box-sizing: border-box !important;
              order: 2 !important;
              /* Reset the desktop fixed flex-basis (0 0 500px) so the content
                 actually shrinks to viewport width on mobile. Without this,
                 flex-basis 500px would lock the content at 500px even when
                 width:100% is set, breaking mobile layout on small screens. */
              flex: 0 0 auto !important;
            }
            .howitworks-content-container h1 {
              max-width: 100% !important;
              word-wrap: break-word !important;
              font-size: 1.5rem !important;
              text-align: center !important;
            }
            .howitworks-content-container h2 {
              font-size: 1.25rem !important;
            }
            .howitworks-content-container h3 {
              font-size: 1rem !important;
            }
            .howitworks-content-container p {
              font-size: 0.9rem !important;
            }
            .howitworks-content-container ul {
              font-size: 0.9rem !important;
            }
            .responsive-svg {
              margin-top: 0rem !important;
              max-width: 100% !important;
              padding: 0 0.5rem !important;
            }
            .hand-svg {
              max-width: 85% !important;
            }
            /* Mobile size override for offer and submit SVGs */
            img[src="/images/howitworksoffer.svg"],
            img[src="/images/howitworkssubmit.svg"] {
              max-width: 55% !important;
            }
            /* Reduce SVG padding and set order on mobile - reset to original padding */
            .howitworks-section-container > div:first-child {
              padding-top: 0.5rem !important;
              order: 1 !important;
            }
            .howitworks-svg-container {
              padding-top: 0.5rem !important;
            }
            /* Reduce gap under header on mobile only */
            .first-section {
              padding-top: 0rem !important;
            }
            /* Fix horizontal scrolling by ensuring all containers fit mobile */
            .max-w-7xl {
              padding-left: 1rem !important;
              padding-right: 1rem !important;
            }
            /* Ensure all content containers are mobile-friendly */
            div[style*="width: 480px"], div[style*="width: 400px"], div[style*="width: 600px"], div[style*="width: 360px"], div[style*="width: 300px"], div[style*="width: 500px"] {
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 1rem !important;
              box-sizing: border-box !important;
            }
            /* Center SVGs on mobile */
            .howitworks-section-container > div:first-child {
              text-align: center !important;
              margin: 0 auto !important;
            }
          }
          
          /* Back to Top button positioning - move left on desktop only */
          @media (min-width: 769px) {
            .back-to-top-btn {
              right: 160px !important;
            }
          }
        `}</style>
      </div>

      {/* Section Divider */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '0' }}></div>
        </div>
      </div>

      {/* Direct Trade Offers Section Header */}
      <div id="direct-trade-offers" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="howitworks-content-container" style={{ textAlign: 'center', marginBottom: '1rem', width: '100%' }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.625rem', 
              fontWeight: 'bold', 
              textAlign: 'center'
            }}>
              Direct trade offers
            </h2>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.25rem', 
              fontWeight: 'normal', 
              marginBottom: '1rem',
              textAlign: 'center',
              marginTop: '1rem',
              maxWidth: '850px',
              margin: '1rem auto 1rem auto'
            }}>
              See a listing you like? Propose a trade with one or more pedals from your Pedalboard. Add or ask for cash to balance the offer.
            </h3>
          </div>
        </div>
      </div>

      {/* Direct Trade Offers - Find a Pedal */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '0.5rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '3.5rem', order: 2 }}>
            <img 
              src="/images/howitworkspedal.svg" 
              alt="Direct Trade Offer Guide"
              className="responsive-svg hand-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none',
                width: '320px'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Find a Pedal
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              From the search bar, Browse Pedals, or Match Opportunities, select a pedal
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Click the <strong>Propose a trade</strong> button on the listing
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Direct Trade Offers - Offer a Pedal */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '2rem', order: 2 }}>
            <img 
              src="/images/howitworksoffer.svg" 
              alt="Choose Pedal Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '55%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Offer a Pedal
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Choose an active pedal from your pedalboard
            </h3>
            
            <p className="text-lg font-semibold text-gray-700" style={{ 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Below the listing and Offer details, is a grid of your active pedals
            </p>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Select up to 4 of your pedals to offer at once, for their pedal
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Direct Trade Offers - Send Offer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '5.5rem', order: 2 }}>
            <img 
              src="/images/howitworkssubmit.svg" 
              alt="Send Offer Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '55%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Send Offer
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Choose your offer type and submit
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Select from Straight Trade, Ask for additional $, or Offer additional $, to propose either a straight trade, to add cash, or to ask for cash alongside your pedal(s) offer
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Review the full cost breakdown, including any platform fees, processing fees, or sales tax
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Hit Submit and wait for a response from the other user!
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '0' }}></div>
        </div>
      </div>

      {/* Direct Purchase Offers Section Header */}
      <div id="direct-purchase-offers" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="howitworks-content-container" style={{ textAlign: 'center', marginBottom: '1rem', width: '100%' }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.625rem', 
              fontWeight: 'bold', 
              textAlign: 'center'
            }}>
              Direct purchase offers
            </h2>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.25rem', 
              fontWeight: 'normal', 
              marginBottom: '1rem',
              textAlign: 'center',
              marginTop: '1rem',
              maxWidth: '850px',
              margin: '1rem auto 1rem auto'
            }}>
              Prefer to buy? Name your price with a cash offer on any listing, no trading required.
            </h3>
          </div>
        </div>
      </div>

      {/* Direct Purchase Offer - Find a Pedal */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '0.5rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '3.5rem', order: 2 }}>
            <img 
              src="/images/howitworkspedal.svg" 
              alt="Direct Purchase Guide"
              className="responsive-svg hand-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none',
                width: '320px'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Find a Pedal
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              From the search bar, Browse Pedals, or Match Opportunities, select a pedal
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Click the <strong>Offer to buy</strong> button on the listing
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Direct Purchase Offer - Send Offer */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '5.5rem', order: 2 }}>
            <img 
              src="/images/howitworkssubmit.svg" 
              alt="Choose Offer Amount Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '55%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Send Offer
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Choose your offer amount and submit
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Enter your proposed cash offer amount
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Review the full cost breakdown, including any platform fees, processing fees, or sales tax
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Hit Submit and wait for a response from the other user!
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '0' }}></div>
        </div>
      </div>

      <div id="trade-matches">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '3rem', paddingBottom: '1rem' }}>
          <div className="howitworks-content-container" style={{ textAlign: 'center', marginBottom: '1rem', width: '100%' }}>
          <h2 style={{
            color: '#C9A227',
            fontSize: '1.625rem',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            Matched trading
          </h2>
          <h3 style={{ 
            color: '#6b7280', 
            fontSize: '1.25rem', 
            fontWeight: 'normal', 
            marginBottom: '1rem',
            textAlign: 'center',
            marginTop: '1rem',
            maxWidth: '850px',
            margin: '1rem auto 1rem auto'
          }}>
            Most trades on PXC start with a direct offer. Occasionally your Pedalboard overlaps with someone else's Wantlist, or theirs with yours, and we flag it as a possible trade. You can find those in the Trading tab.
          </h3>
          </div>
        </div>

      {/* First Section - List Your Pedal */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8 first-section" style={{ paddingTop: '0.5rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div className="howitworks-svg-container" style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '10rem', order: 2 }}>
            <img 
              src="/images/howitworkspedal.svg" 
              alt="How It Works Pedal Guide"
              className="responsive-svg hand-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none',
                width: '320px'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              List Your Pedal
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Let people know what you've got to trade
            </h3>
            
            <p className="text-lg font-semibold text-gray-700" style={{ 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              From the navigation bar, select Add Pedal
            </p>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left',
              marginBottom: '1.5rem'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Add product details like Brand, Model, Condition, and a description
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Upload clear photos – include images of any damage or accessories
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Click Submit – your pedal is live and ready for offers
              </li>
            </ul>
            
          </div>
        </div>
      </div>

      {/* Second Section - Wantlist */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div className="howitworks-svg-container" style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '10rem', order: 2 }}>
            <img 
              src="/images/howitworkshand.svg" 
              alt="How It Works Wantlist Guide"
              className="responsive-svg hand-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none',
                width: '320px'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Build your Wantlist
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Tell us what you're looking for
            </h3>
            
            <p className="text-lg font-semibold text-gray-700" style={{ 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Tap the heart on any pedal, hit Save to Wantlist on a listing, or type one in under My Pedals. We'll notify you when one gets listed
            </p>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left',
              marginBottom: '1.5rem'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Enter details like Brand, Model, Condition, Price, and even Year
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Add as many as you like, you can remove them anytime
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Turn on email notifications for those must-have pedals.
              </li>
            </ul>
            
          </div>
        </div>
      </div>

      {/* Third Section - Review Matches */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div className="howitworks-svg-container" style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '10rem', order: 2 }}>
            <img 
              src="/images/howitworksmatch.svg" 
              alt="How It Works Match Review Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Review Matches
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              If your Wantlist and someone's Pedalboard overlap, we flag the possible two-way trade
            </h3>
            
            <p className="text-lg font-semibold text-gray-700" style={{ 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              In the Trading hub, open the Matches tile to see your trade matches, and then send an offer when one looks good.
            </p>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left',
              marginBottom: '1.5rem'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Each trade match shows
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                The pedal you would give
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                The pedal you would receive
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                The condition and asking price of each pedal
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Filter the results by Brand, Model, Price, Condition, or Decade
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Select <strong>View Match Details</strong> to learn more about the match and to submit offers
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                Click the <strong>Make a trade offer</strong> button
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                Select your offer type, and click <strong>+Add additional pedals</strong> to offer up to 4 pedals in trade
              </li>
            </ul>
            
          </div>
        </div>
      </div>

      {/* Fourth Section - Confirm Trade */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div className="howitworks-section-container" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          maxWidth: '1100px', 
          margin: '0 auto', 
          width: '100%', 
          justifyContent: 'center', 
          alignItems: 'flex-start', 
          gap: '1.5rem' 
        }}>
          {/* SVG Section */}
          <div style={{ width: '300px', textAlign: 'center', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '3.5rem', order: 2 }}>
            <img 
              src="/images/howitworkstrade.svg" 
              alt="How It Works Trade Confirmation Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Content Section */}
          <div className="howitworks-content-container" style={{ textAlign: 'left', width: '500px', flex: '0 0 500px', order: 1 }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Confirm Trade
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Accept an offer or send one from a trade match
            </h3>
            
            <p className="text-lg font-semibold text-gray-700" style={{ 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Find your accepted trades and purchases from the Trading hub to complete the Trade or Purchase Progress checklist
            </p>
            
            <ul className="text-lg font-semibold text-gray-700" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Pay any fees, additional coverage, or balance differences negotiated in the trade
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Upload a confirmation photo of the pedal with the packaging
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Confirm that you are ready to ship (pedal is fully packaged)
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Buy your shipping label directly on the platform
              </li>
              <li style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                Each user buys their own shipping label directly through PXC. All labels are fully insured and offered at discounted rates through our bulk shipping partnerships.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Track all shipments via the platform
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Leave trade feedback for other users to see
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Enjoy your new tones!
              </li>
            </ul>
          </div>
        </div>
      </div>
      </div>

      {/* Fee Structure Link */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '4rem', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <Link 
              to="/fees" 
              style={{
                fontFamily: 'Nunito, sans-serif',
                color: '#5A9ECB',
                fontWeight: 'bold',
                fontSize: '16px',
                textDecoration: 'none'
              }}
              className="hover:opacity-80 transition-opacity"
            >
              View Membership and Fee Structure
            </Link>
          </div>
        </div>
      </div>

      {/* Sign Up Button */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            {firestoreUser ? (
              <Link 
                to="/dashboard" 
                onClick={() => window.scrollTo(0, 0)}
                className="hover:opacity-80 transition-opacity"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  backgroundColor: '#C9A227',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Back to Dashboard
              </Link>
            ) : (
              <Link 
                to="/signup" 
                className="hover:opacity-80 transition-opacity"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  backgroundColor: '#C9A227',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Sign Up Here
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4" style={{ paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default HowItWorks; 